"use client"

import { CSSProperties, ReactNode, useState } from "react"
import { ChevronRight } from "lucide-react"

// Local Career UI compositions over the .smx kit tokens (sanctioned pattern:
// compose, don't re-implement kit primitives). These read the platform palette
// so they theme (light/dark) automatically inside the MaxWorkspaceShell .smx scope.

const sectionBoxStyle: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 14,
  padding: "18px 20px",
  marginBottom: 16,
}

export function Section({ title, description, actions, children, collapsible, defaultOpen = true }: { title?: string; description?: string; actions?: ReactNode; children: ReactNode; collapsible?: boolean; defaultOpen?: boolean }) {
  const boxStyle = sectionBoxStyle

  // Collapsible variant: a rotating chevron header, standardized on the kit's CollapsibleSection
  // pattern (a lucide chevron that rotates 90deg open, an accessible <button> with aria-expanded)
  // so every foldable section across the app reads the same.
  if (collapsible && title) {
    return <CollapsibleSectionBox title={title} description={description} actions={actions} defaultOpen={defaultOpen}>{children}</CollapsibleSectionBox>
  }

  return (
    <section style={boxStyle}>
      {(title || actions) && (
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: description ? 4 : 12 }}>
          {title && <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "var(--text-primary)" }}>{title}</h2>}
          {actions && <div style={{ marginLeft: "auto" }}>{actions}</div>}
        </div>
      )}
      {description && <p style={{ fontSize: 13.5, color: "var(--text-muted)", margin: "0 0 14px" }}>{description}</p>}
      {children}
    </section>
  )
}

// The foldable Section body. A real button + aria-expanded for keyboard/screen-reader access; the
// chevron rotates like the kit's CollapsibleSection. When collapsed, only the title row shows so a
// reader still sees THAT there is a section, just not its detail.
function CollapsibleSectionBox({ title, description, actions, defaultOpen, children }: { title: string; description?: string; actions?: ReactNode; defaultOpen: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <section style={sectionBoxStyle}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: open ? (description ? 4 : 12) : 0 }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", border: "none", padding: 0, margin: 0, cursor: "pointer", font: "inherit", textAlign: "left", color: "var(--text-primary)" }}
        >
          <ChevronRight size={16} aria-hidden style={{ flexShrink: 0, color: "var(--text-muted)", transition: "transform 120ms ease", transform: open ? "rotate(90deg)" : "none" }} />
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "var(--text-primary)" }}>{title}</h2>
        </button>
        {actions && <div style={{ marginLeft: "auto" }}>{actions}</div>}
      </div>
      {open && description && <p style={{ fontSize: 13.5, color: "var(--text-muted)", margin: "0 0 14px" }}>{description}</p>}
      {open && children}
    </section>
  )
}

export const labelStyle: CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--text-muted)",
  marginBottom: 4,
  letterSpacing: "0.01em",
}

export function inputStyle(extra?: CSSProperties): CSSProperties {
  return {
    width: "100%",
    padding: "8px 10px",
    fontSize: 14,
    borderRadius: 9,
    border: "1px solid var(--border-subtle)",
    background: "var(--surface-sunken, var(--surface))",
    color: "var(--text-primary)",
    outline: "none",
    ...extra,
  }
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={labelStyle}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

export function Grid({ cols = 2, children }: { cols?: number; children: ReactNode }) {
  return <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 12 }}>{children}</div>
}

type PillTone = "neutral" | "success" | "warning" | "info" | "accent"
const PILL_TONES: Record<PillTone, { fg: string; bg: string; bd: string }> = {
  neutral: { fg: "var(--text-muted)", bg: "var(--surface-sunken, transparent)", bd: "var(--border-subtle)" },
  success: { fg: "var(--status-success-text)", bg: "rgba(61,190,122,0.12)", bd: "var(--status-success-border)" },
  warning: { fg: "var(--status-warning-text)", bg: "rgba(245,165,95,0.12)", bd: "var(--status-warning-border)" },
  info: { fg: "var(--status-info-text)", bg: "rgba(120,160,220,0.12)", bd: "var(--status-info-border)" },
  accent: { fg: "var(--accent-text)", bg: "rgba(241,48,9,0.12)", bd: "rgba(241,48,9,0.3)" },
}

export function Pill({ tone = "neutral", children }: { tone?: PillTone; children: ReactNode }) {
  const t = PILL_TONES[tone]
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 999,
        color: t.fg,
        background: t.bg,
        border: `1px solid ${t.bd}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  )
}

export function StatTile({ label, value, hint, href }: { label: string; value: ReactNode; hint?: string; href?: string }) {
  const inner = (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 12,
        padding: "14px 16px",
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>{typeof value === "number" ? value.toLocaleString("en-US") : value}</div>
      <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      {hint && <div style={{ fontSize: 11.5, color: "var(--accent-text)", marginTop: 6 }}>{hint}</div>}
    </div>
  )
  return href ? (
    <a href={href} style={{ textDecoration: "none", display: "block" }}>
      {inner}
    </a>
  ) : (
    inner
  )
}

export function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: "18px", textAlign: "center", color: "var(--text-muted)", fontSize: 13.5, border: "1px dashed var(--border-subtle)", borderRadius: 12 }}>
      {children}
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 13, color: "var(--status-danger-text)", background: "rgba(214,59,59,0.10)", border: "1px solid var(--status-danger-border)", borderRadius: 9, padding: "8px 12px", marginBottom: 12 }}>
      {children}
    </div>
  )
}

// comma-separated array <-> string helpers for tag-ish fields
export function toTags(v: string): string[] {
  return v.split(",").map((s) => s.trim()).filter(Boolean)
}
export function fromTags(v: unknown): string {
  return Array.isArray(v) ? v.join(", ") : ""
}
